import { useState } from 'react';
import type { AccountSummary, SupportAccess, SupportVisit } from '../../api/admin';
import { Modal } from '../../components/fs';
import { longDate } from './planDisplay';
import { usePlatform } from './PlatformLayout';

/**
 * The account page's dialogs (docs/PLAYBOOK-account-overview.md, PR 1): enter
 * in support mode, change status, delete (which schedules), Delete now, and
 * invite a user. One is open at a time; each says what it will do before its
 * button, and Escape, ✕ or the scrim close it with nothing saved. On a phone
 * each is a bottom sheet (fs.tsx Modal).
 */

// ---------------------------------------------------------------------------
// Support mode
// ---------------------------------------------------------------------------

const REASON_CHIPS = ['Support ticket', 'Bug investigation', 'Onboarding help', 'Billing question'];

/**
 * Whether a reason says why — the server's own rule (lib/supportMode.ts): more
 * than three characters beyond a quick chip's "Support ticket: ".
 */
export function supportReasonValid(reason: string): boolean {
  return reason.trim().replace(/^[^:]{0,40}:\s*/, '').trim().length > 3;
}

/**
 * Enter an account as its owner sees it (074). Read-only is the default:
 * every save is refused by the server for the whole visit. Every staff rung
 * may choose full access too (Ben, 2026-09-26). A reason is required, and the
 * dialog says where it goes before anyone types it.
 */
export function SupportModeModal({ account, busy, error, onClose, onEnter }: {
  account: Pick<AccountSummary, 'name'>;
  busy: boolean;
  error: string;
  onClose: () => void;
  onEnter: (visit: SupportVisit) => void;
}) {
  const [access, setAccess] = useState<SupportAccess>('read');
  const [reason, setReason] = useState('');
  const [ref, setRef] = useState('');
  const valid = supportReasonValid(reason);
  const read = access === 'read';
  const enter = () => onEnter({ access, reason: reason.trim(), ...(ref.trim() ? { ref: ref.trim() } : {}) });

  return (
    <Modal
      title={`Enter ${account.name} in support mode`}
      sub="You’ll see the account the way its owner does."
      className="pl-ao-modal-support"
      onClose={onClose}
      footer={(
        <>
          <button className="fs-btn primary" disabled={!valid || busy} onClick={enter}>
            {read ? 'Enter read-only ↗' : 'Enter with full access ↗'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
          {!valid && <span className="pl-ao-foot-hint">Give a reason to continue.</span>}
        </>
      )}
    >
      <div className="fs-section-label" id="ao-access">Access</div>
      <div role="radiogroup" aria-labelledby="ao-access">
        {([
          ['read', 'Read-only', 'Look at forms, responses and settings. Nothing you do can be saved.'],
          ['full', 'Full access', 'Change forms, settings and users as if you were the owner.'],
        ] as const).map(([value, title, text]) => (
          <label key={value} className={`fs-choice${access === value ? ' on' : ''}`}>
            <input type="radio" name="ao-access" value={value} checked={access === value}
              onChange={() => setAccess(value)} />
            <span><b>{title}</b><small>{text}</small></span>
          </label>
        ))}
      </div>

      <div className="fs-field pl-ao-field">
        <label htmlFor="ao-reason">Reason <span className="pl-optional">(required)</span></label>
        <div className="pl-ao-chips" role="group" aria-label="Quick reasons">
          {REASON_CHIPS.map((chip) => (
            <button key={chip} type="button" className="pl-ao-chip"
              onClick={() => setReason((r) => (r.trim() ? r : `${chip}: `))}>
              {chip}
            </button>
          ))}
        </div>
        <textarea id="ao-reason" className="pl-ao-reason" value={reason} placeholder="What are you going in to do?"
          onChange={(e) => setReason(e.target.value)} />
      </div>
      <div className="fs-field pl-ao-field">
        <label htmlFor="ao-ref">Ticket or reference</label>
        <input id="ao-ref" value={ref} placeholder="Optional — e.g. #4821" onChange={(e) => setRef(e.target.value)} />
      </div>

      <div className="fs-consequence pl-ao-consequence">
        Your name, the access level and this reason go into {account.name}’s audit log and the platform
        log. {read ? 'Read-only blocks every save for the whole visit.' : 'Full access can change anything the owner can.'}
      </div>
      {error && <div className="ad-err" role="alert">{error}</div>}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

// What can be chosen (review finding 24). 'trial' and 'past_due' keep their
// words below for the trail's older entries; neither is a status any more —
// a trial and a declined card are billing's to say, and the pills read them there.
export const ACCOUNT_STATUSES = ['active', 'suspended', 'cancelled'] as const;

export const STATUS_WORD: Record<string, string> = {
  trial: 'Trial', active: 'Active', past_due: 'Past due', suspended: 'Suspended', cancelled: 'Cancelled',
};

/**
 * What each status does, said as the server behaves (`accountActive`):
 * suspended and cancelled both stop sign-in and the public forms, and neither
 * touches billing. (The console used to say a cancelled account "keeps its
 * data but nothing renews" — nothing about renewal ever changed.)
 */
export const STATUS_CONSEQUENCE: Record<string, string> = {
  suspended: 'Suspended accounts cannot sign in and their public forms stop accepting submissions.',
  cancelled: 'Cancelled accounts cannot sign in and their public forms stop accepting submissions. '
    + 'Billing is not changed — end the subscription with Change plan.',
  active: 'The account runs normally.',
  trial: 'The account is marked as trialling.',
  past_due: 'Marks the account as behind on payment.',
};

/** Move an account's status — five radio rows, the current one marked, the consequence before Save. */
export function StatusModal({ account, busy, error, onClose, onSave }: {
  account: Pick<AccountSummary, 'name' | 'status'>;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSave: (status: string) => void;
}) {
  const [status, setStatus] = useState(account.status);
  const changed = status !== account.status;
  return (
    <Modal title={`Change status — ${account.name}`} onClose={onClose}
      footer={(
        <>
          <button className="fs-btn primary" disabled={!changed || busy} onClick={() => onSave(status)}>Save status</button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      <div className="fs-section-label" id="ao-status">Status</div>
      <div role="radiogroup" aria-labelledby="ao-status" className="pl-ao-status-rows">
        {ACCOUNT_STATUSES.map((s) => (
          <label key={s} className={`fs-choice pl-ao-status-row${status === s ? ' on' : ''}`}>
            <input type="radio" name="ao-status" value={s} checked={status === s} onChange={() => setStatus(s)} />
            <span><b>{STATUS_WORD[s]}</b></span>
            {s === account.status && <span className="pl-ao-current">Current</span>}
          </label>
        ))}
      </div>
      <div className="fs-consequence pl-ao-consequence">
        {changed
          ? <><b>{STATUS_WORD[account.status] ?? account.status} → {STATUS_WORD[status]}.</b> {STATUS_CONSEQUENCE[status]} The change lands in the audit trail under your name.</>
          : 'Pick a different status to move this account.'}
      </div>
      {error && <div className="ad-err" role="alert">{error}</div>}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Delete (which schedules) and Delete now
// ---------------------------------------------------------------------------

/**
 * Delete an account — which SCHEDULES it (074): suspended now, billing paused,
 * deleted for good after 30 days unless an Admin restores it. The account's
 * name typed back is the confirmation, as it always was.
 */
export function DeleteAccountModal({ account, deleteOn, busy, error, onClose, onDelete }: {
  account: Pick<AccountSummary, 'name'>;
  /** the day it would be deleted — today + 30 */
  deleteOn: string;
  busy: boolean;
  error: string;
  onClose: () => void;
  onDelete: () => void;
}) {
  const [typed, setTyped] = useState('');
  const match = typed.trim() === account.name;
  return (
    <Modal title={`Delete ${account.name}?`} className="pl-ao-modal-delete" onClose={onClose}
      footer={(
        <>
          <button className="fs-btn danger solid" disabled={!match || busy} onClick={onDelete}>Schedule deletion</button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      <div className="fs-consequence danger">
        <b>{account.name}</b> is suspended now and permanently deleted on <b>{longDate(deleteOn)}</b> — every form,
        submission and user in it. Until then any Admin can restore it.
      </div>
      <ul className="pl-ao-dashes">
        <li>Public forms stop accepting submissions straight away.</li>
        <li>Billing is paused, so nothing is charged.</li>
        <li>The owner and billing contact are emailed that the account is closing.</li>
      </ul>
      <div className="fs-field">
        <label htmlFor="ao-delete-name">Type the account name to confirm</label>
        <input id="ao-delete-name" autoFocus placeholder={account.name} value={typed} onChange={(e) => setTyped(e.target.value)} />
      </div>
      {error && <div className="ad-err" role="alert">{error}</div>}
    </Modal>
  );
}

/**
 * Delete now (Ben, 2026-09-26): a Super Admin finishes a deletion that is
 * already scheduled — a test account, an erasure request. It cannot be undone,
 * so it asks for the name again.
 */
export function DeleteNowModal({ account, busy, error, onClose, onDelete }: {
  account: Pick<AccountSummary, 'name'>;
  busy: boolean;
  error: string;
  onClose: () => void;
  onDelete: (typedName: string) => void;
}) {
  const [typed, setTyped] = useState('');
  const match = typed.trim() === account.name;
  // The deletion grace is a platform rule (Platform › Rules); the code's number stands in.
  const graceDays = usePlatform().rules?.deleteGraceDays ?? 30;
  return (
    <Modal title={`Delete ${account.name} now?`} className="pl-ao-modal-delete" onClose={onClose}
      footer={(
        <>
          <button className="fs-btn danger solid" disabled={!match || busy} onClick={() => onDelete(typed.trim())}>Delete now</button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      <div className="fs-consequence danger">
        This deletes <b>{account.name}</b> permanently, today, instead of when its {graceDays} days are up — every form,
        submission and user in it. It cannot be restored.
      </div>
      <ul className="pl-ao-dashes">
        <li>Their Stripe subscription is cancelled, with no refund.</li>
        <li>Their uploaded files and custom domains are released.</li>
      </ul>
      <div className="fs-field">
        <label htmlFor="ao-purge-name">Type the account name to confirm</label>
        <input id="ao-purge-name" autoFocus placeholder={account.name} value={typed} onChange={(e) => setTyped(e.target.value)} />
      </div>
      {error && <div className="ad-err" role="alert">{error}</div>}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Invite a user
// ---------------------------------------------------------------------------

/**
 * Provision a login inside a customer's account.
 *
 * No password field: a member of staff who typed a customer's password would
 * be holding a credential to somebody else's business. They are emailed a link
 * and choose their own.
 */
export function AddUserModal({ account, onClose, onAdd }: {
  account: Pick<AccountSummary, 'name'>;
  onClose: () => void;
  onAdd: (body: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({ email: '', name: '' });
  const valid = !!form.email.trim();
  return (
    <Modal title={`Invite a user — ${account.name}`} narrow onClose={onClose}
      footer={(
        <>
          <button className="fs-btn primary" disabled={!valid} onClick={() => onAdd(form)}>Send invitation</button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      <div className="fs-row-2">
        <div className="fs-field">
          <label htmlFor="pl-user-email">Email</label>
          <input id="pl-user-email" type="email" autoFocus value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="fs-field">
          <label htmlFor="pl-user-name">Name</label>
          <input id="pl-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
      </div>
      <div className="fs-consequence pl-modal-note">
        This creates a login inside the customer's account and emails them a link to choose their
        own password — you will not know it. The addition lands in the audit trail under your name.
      </div>
    </Modal>
  );
}
